import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  Announcement, AnnouncementsListResponse, CreateAnnouncementPayload,
  ScheduleAnnouncementPayload, TierUpdatePayload, TierPriceUpdatePayload, PlatformStats,
  AddonPrice,
} from '../models/api.models';

const BASE = environment.apiBaseUrl + '/v1/admin';

@Injectable({ providedIn: 'root' })
export class AnnouncementsService {
  private readonly http = inject(HttpClient);

  readonly unreadCount = signal(0);

  // ── User-facing ────────────────────────────────────────────────────────
  listPublished() {
    return this.http.get<AnnouncementsListResponse>(`${BASE}/announcements`);
  }

  loadUnreadCount(): void {
    this.http.get<{ count: number }>(`${BASE}/me/announcements/unread-count`)
      .subscribe({ next: (r) => this.unreadCount.set(r.count), error: () => {} });
  }

  markRead(id: string) {
    return this.http.post<{ status: string }>(`${BASE}/me/announcements/${id}/read`, {});
  }

  markAllRead() {
    return this.http.post<{ status: string }>(`${BASE}/me/announcements/read-all`, {});
  }

  // ── Platform admin ─────────────────────────────────────────────────────────
  listAll() {
    return this.http.get<{ announcements: Announcement[] }>(`${BASE}/platform-admin/announcements`);
  }

  create(payload: CreateAnnouncementPayload) {
    return this.http.post<{ announcement: Announcement }>(`${BASE}/platform-admin/announcements`, payload);
  }

  update(id: string, payload: CreateAnnouncementPayload) {
    return this.http.patch<{ announcement: Announcement }>(`${BASE}/platform-admin/announcements/${id}`, payload);
  }

  publish(id: string) {
    return this.http.post<{ announcement: Announcement }>(`${BASE}/platform-admin/announcements/${id}/publish`, {});
  }

  schedule(id: string, payload: ScheduleAnnouncementPayload) {
    return this.http.post<{ announcement: Announcement }>(`${BASE}/platform-admin/announcements/${id}/schedule`, payload);
  }

  delete(id: string) {
    return this.http.delete<{ status: string }>(`${BASE}/platform-admin/announcements/${id}`);
  }

  // ── Tier editor ────────────────────────────────────────────────────────
  saveTiers(tiers: TierUpdatePayload[]) {
    return this.http.put<{ status: string }>(`${BASE}/platform-admin/tiers`, { tiers });
  }

  savePrices(prices: TierPriceUpdatePayload[]) {
    return this.http.put<{ status: string }>(`${BASE}/platform-admin/tier-prices`, { prices });
  }

  // ── Addon prices ───────────────────────────────────────────────────────────
  getAddonPrices() {
    return this.http.get<{ prices: AddonPrice[] }>(`${BASE}/platform-admin/addon-prices`);
  }

  saveAddonPrices(prices: AddonPrice[]) {
    return this.http.put<void>(`${BASE}/platform-admin/addon-prices`, { prices });
  }

  // ── Platform stats ─────────────────────────────────────────────────────
  platformStats() {
    return this.http.get<{ stats: PlatformStats }>(`${BASE}/platform-admin/platform/stats`);
  }
}
